import type { GetAppointmentRatesResponse } from '#services/api/insights.ts';

import { Box, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
} from '@mui/x-charts-pro';
import { useQuery } from '@tanstack/react-query';

import { appointmentRates } from '#lib/react-query/queries.ts';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import {
  CHART_AXIS_STYLES,
  CHART_SERIES_COLORS,
  CHART_X_AXIS_CONFIG,
  WIDGET_CHART_HEIGHT,
} from '#utils/charts.ts';
import { Base } from './Base';
import { EmptyDataOverlay } from './EmptyDataOverlay.tsx';
import { useAppointmentWidget } from './use-appointment-widget.ts';

const widgetTitle = 'Appointment Rates';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows the breakdown of scheduled appointments and their outcomes — confirmed, completed,
        canceled, and no-showed. This view helps leaders track patient engagement, spot trends, and
        assess operational performance across scheduling and follow-through.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoList
        items={[
          <>
            <InfoEmphasis>Total Appointments</InfoEmphasis>
            &nbsp;= All appointments within the selected date range, excluding deleted
          </>,
          <>
            <InfoEmphasis>Confirmed</InfoEmphasis>
            &nbsp;= Appointment confirmed by patient or staff before the appointment start time
            (confirmations after start time may still appear but are not guaranteed to be excluded)
          </>,
          <>
            <InfoEmphasis>Canceled</InfoEmphasis>
            &nbsp;= Appointment canceled before the appointment start time (cancellations after
            start time may still appear but are not guaranteed to be excluded)
          </>,
          <>
            <InfoEmphasis>No-Showed</InfoEmphasis>
            &nbsp;= Appointment marked as no-show
          </>,
          <>
            If an appointment is both no-show and canceled, it is counted as&nbsp;
            <InfoEmphasis>canceled only</InfoEmphasis>
          </>,
        ]}
      />

      <InfoSection title="Rates" indented sx={{ mb: 2 }}>
        <InfoList
          items={[
            <>
              <InfoEmphasis>Confirmation Rate</InfoEmphasis>
              &nbsp;= Confirmed ÷ (Total Appointments − Canceled)
            </>,
            <>
              <InfoEmphasis>Cancellation Rate</InfoEmphasis>
              &nbsp;= Canceled ÷ Total Appointments (deleted appointments are not excluded in this
              calculation)
            </>,
            <>
              <InfoEmphasis>No-Show Rate</InfoEmphasis>
              &nbsp;= No-Showed ÷ (Total Appointments − Canceled)
            </>,
          ]}
        />
      </InfoSection>

      <Typography variant="body2" sx={{ pl: 5, mb: 2 }}>
        All calculations respect applied date and other appointment filters.
      </Typography>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Appointments scheduled within the selected date range',
            'Appointments with valid provider and metadata needed for filtering',
            'Appointments of any status (confirmed, canceled, no-showed, completed)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            <>
              Deleted appointments (from totals and most metrics, but see&nbsp;
              <Typography component="span" variant="inherit" fontStyle="italic">
                Cancellation Rate
              </Typography>
              &nbsp;note above)
            </>,
            'Appointments outside the selected date range',
            'Appointments missing provider or filtering metadata',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            <>
              Deleted appointments are always excluded from totals, confirmation, and no-show
              metrics — but{' '}
              <InfoEmphasis>not guaranteed to be excluded from cancellation</InfoEmphasis>
              &nbsp;rate
            </>,
            'Confirmation does not guarantee attendance — confirmed appointments may still be canceled or no-showed',
            'No-show and canceled are mutually exclusive for reporting',
            'Late cancellations still count as canceled, even if the patient did not attend',
            'Confirmed and canceled counts are not guaranteed to exclude actions taken after the appointment start time',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

function select(response: GetAppointmentRatesResponse) {
  return {
    ...response,
    data: response.data.map((item) => {
      return {
        ...item,
        period: new Date(item.period),
      };
    }),
  };
}

export function AppointmentRates() {
  const widget = useAppointmentWidget();

  const theme = useTheme();

  // Fetch form trends data
  const { data, isLoading } = useQuery({
    ...appointmentRates(widget.getCommonRequestParams()),
    select,
  });

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <Base
      title={widgetTitle}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    >
      {(isOpenedInDialog) => (
        <Box height="100%">
          {data && data.data.length > 0 ? (
            <ChartDataProvider
              colors={(mode) => CHART_SERIES_COLORS[mode]}
              dataset={data.data}
              height={isOpenedInDialog ? undefined : WIDGET_CHART_HEIGHT}
              // Need to set right marin to fit x-axis tick label
              margin={{ right: 30 }}
              series={[
                {
                  dataKey: 'confirmationRate',
                  label: 'Confirmation Rate',
                  labelMarkType: 'circle',
                  type: 'line',
                  valueFormatter: (value) => `${value}%`,
                },
                {
                  dataKey: 'noShowRate',
                  label: 'No-show Rate',
                  labelMarkType: 'circle',
                  type: 'line',
                  valueFormatter: (value) => `${value}%`,
                },
                {
                  dataKey: 'cancellationRate',
                  label: 'Cancellation Rate',
                  labelMarkType: 'circle',
                  type: 'line',
                  valueFormatter: (value) => `${value}%`,
                },
              ]}
              xAxis={[
                {
                  dataKey: 'period',
                  height: 60,
                  label: 'Dates',
                  scaleType: 'time',
                  ...CHART_X_AXIS_CONFIG[data.groupedBy],
                },
              ]}
              yAxis={[
                {
                  label: 'Rate',
                  valueFormatter: (value: number) => `${value}%`,
                },
              ]}
            >
              <ChartsLegend
                sx={{
                  justifyContent: 'center',
                  '.MuiChartsLegend-label': {
                    color: theme.palette.muted[4],
                  },
                }}
              />
              <ChartsSurface>
                <ChartsXAxis sx={CHART_AXIS_STYLES(theme)} />
                <ChartsYAxis sx={CHART_AXIS_STYLES(theme)} />
                <LinePlot />
                <MarkPlot />
                <ChartsAxisHighlight x="line" y="none" />
                <ChartsTooltip />
              </ChartsSurface>
            </ChartDataProvider>
          ) : (
            <EmptyDataOverlay variant={isOpenedInDialog ? 'dialog' : 'chart'} />
          )}
        </Box>
      )}
    </Base>
  );
}
